import { z } from 'zod';
import { SortDirectionSchema, PaginationSchema } from './common.js';
/**
 * 產品類型 ENUM
 */
export const ProductTypeSchema = z.enum([
    'physical',
    'service',
    'voucher',
    'subscription',
    'bundle',
]);
/**
 * 套餐明細（單一子商品 + 數量）
 */
export const BundleItemSchema = z.object({
    product_id: z.string().uuid().describe('子商品 ID'),
    quantity: z.number().int().min(1).describe('數量'),
    sort_order: z.number().int().optional().describe('顯示順序'),
});
/**
 * 套餐明細（含子商品名稱，用於 API 回應）
 */
export const BundleItemWithProductSchema = BundleItemSchema.extend({
    product_name: z.string().optional().describe('子商品名稱'),
});
/**
 * 推薦標籤 ENUM（對應 lucide-react icon）
 * 主廚推薦：chef_hat, thumbs_up, star
 * 熱門促銷：flame, crown, new
 * 食材特性：leaf, fork_knife, medal
 */
export const RecommendationBadgeSchema = z
    .enum([
    'chef_hat',
    'thumbs_up',
    'star',
    'flame',
    'crown',
    'new',
    'leaf',
    'fork_knife',
    'medal',
])
    .nullable()
    .optional();
/**
 * 產品 metadata 結構（按類型區分）
 */
export const PhysicalProductMetadataSchema = z
    .object({
    weight: z.number().int().min(0).optional().describe('重量（克）'),
    is_inventory_tracked: z
        .boolean()
        .optional()
        .default(false)
        .describe('是否追蹤庫存'),
    safety_stock: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('安全庫存（僅在追蹤庫存時需要）'),
    avg_cost: z.number().min(0).optional().describe('平均成本（元）'),
    stock_quantity: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('當前庫存量（僅在追蹤庫存時需要）'),
})
    .refine(data => {
    // 如果追蹤庫存，則 stock_quantity 和 safety_stock 必須提供
    if (data.is_inventory_tracked === true) {
        return (data.stock_quantity !== undefined &&
            data.safety_stock !== undefined &&
            data.stock_quantity >= 0 &&
            data.safety_stock >= 0);
    }
    // 如果不追蹤庫存，則不需要這些欄位
    return true;
}, {
    message: '追蹤庫存時，當前庫存量和安全庫存為必填',
    path: ['stock_quantity'], // 錯誤路徑
})
    .passthrough(); // 允許額外欄位
/** 日期字串 YYYY-MM-DD（商品級適用日期，一商品一場次時使用） */
const DateStringSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, '須為 YYYY-MM-DD')
    .optional();
/** 時段字串 HH:mm 或 HH:mm:ss */
const TimeStringSchema = z
    .string()
    .regex(/^\d{2}:\d{2}(:\d{2})?$/, '須為 HH:mm 或 HH:mm:ss')
    .optional();
export const VoucherProductMetadataSchema = z
    .object({
    valid_days: z.number().int().min(0).describe('有效天數（0=不過期）'),
    max_total_quantity: z
        .number()
        .int()
        .min(1)
        .optional()
        .describe('數量限制（張）'),
    is_transferable: z.boolean().optional().describe('是否可轉讓'),
    /** 適用日期（一商品一場次時設定；有值則該商品發出的券僅當日可核銷） */
    applicable_date: DateStringSchema.describe('適用日期（YYYY-MM-DD）'),
    /** 適用時段起（與 applicable_time_end 同時有值時，核銷須在區間內） */
    applicable_time_start: TimeStringSchema.describe('適用時段起'),
    /** 適用時段訖 */
    applicable_time_end: TimeStringSchema.describe('適用時段訖'),
})
    .passthrough();
export const ServiceProductMetadataSchema = z
    .object({
    valid_days: z.number().int().min(0).describe('有效天數（0=不過期）'),
    package_count: z.number().int().min(1).describe('包含次數'),
})
    .passthrough();
export const SubscriptionProductMetadataSchema = z
    .object({
    valid_days: z.number().int().min(0).describe('有效天數（0=不過期）'),
    access_level: z.string().optional().describe('權限等級（如 "vip"）'),
})
    .passthrough();
/**
 * 產品 metadata Schema（通用，根據 type 動態驗證）
 */
export const ProductMetadataSchema = z
    .union([
    PhysicalProductMetadataSchema,
    VoucherProductMetadataSchema,
    ServiceProductMetadataSchema,
    SubscriptionProductMetadataSchema,
])
    .nullable()
    .optional();
/**
 * 規格定義（規格名 + 規格值陣列）
 */
export const ProductSpecSchema = z.object({
    name: z.string().min(1).max(50).describe('規格名稱（如顏色、尺寸）'),
    options: z
        .array(z.string().min(1).max(100))
        .min(1)
        .describe('規格選項（如紅色、S、M、L）'),
});
/**
 * 規格組合（動態 key，例如 { "顏色": "紅色", "尺寸": "S" }）
 */
export const VariantCombinationSchema = z
    .record(z.string(), z.string())
    .describe('規格組合（規格名 → 規格值）');
/**
 * 商品規格（單一 variant）寫入/讀取
 */
export const ProductVariantSchema = z.object({
    id: z.string().uuid().optional().describe('規格 ID（編輯時回傳）'),
    combination: VariantCombinationSchema.describe('規格組合'),
    sku: z.string().min(1).max(100).describe('規格 SKU'),
    list_price_twd: z.number().int().min(0).describe('定價 (分)'),
    unit_price_twd: z.number().int().min(0).describe('售價 (分)'),
    stock_quantity: z.number().int().min(0).default(0).describe('庫存量'),
    safety_stock: z.number().int().min(0).optional().describe('安全庫存'),
    avg_cost: z.number().int().min(0).optional().describe('平均成本 (分)'),
    is_inventory_tracked: z.boolean().default(true).describe('是否追蹤庫存'),
    img_url: z
        .string()
        .optional()
        .or(z.literal(''))
        .describe('規格圖片 URL（可為空）'),
    sort_order: z.number().int().min(0).optional().describe('排序'),
});
/**
 * 產品排序欄位枚舉
 */
export const ProductSortBySchema = z.enum([
    'id',
    'sku',
    'name',
    'category',
    'list_price_twd',
    'unit_price_twd',
    'created_at',
    'updated_at',
    'current_month_sales',
    'last_month_sales',
    'stock_quantity',
    'safety_stock',
    'avg_cost',
]);
/**
 * 產品基本資訊 Schema
 * 注意：為了向後兼容，API 回應仍包含舊欄位名稱（從 metadata 映射）
 */
export const ProductSchema = z.object({
    id: z.string().uuid().describe('產品 ID（UUID v7）'),
    sku: z.string().min(1).max(50).describe('產品 SKU'),
    category: z
        .string()
        .max(100)
        .nullable()
        .describe('產品分類（主要/向後兼容，等同 categories[0]）'),
    /** 多分類：同一商品可屬於多個分類，樹狀選單會在每個分類下顯示 */
    categories: z
        .array(z.string().max(100))
        .optional()
        .describe('產品所屬分類列表'),
    name: z.string().min(1).max(200).describe('產品名稱'),
    description: z.string().max(1000).nullable().describe('產品描述'),
    img_url: z
        .string()
        .nullable()
        .describe('產品主圖 URL（可以是完整 URL 或相對路徑）'),
    /** 多張圖片 URL（主圖為 img_url；此為輪播/相簿用） */
    image_urls: z
        .array(z.string())
        .optional()
        .describe('多張圖片 URL 列表（主圖為 img_url）'),
    list_price_twd: z.number().int().min(0).describe('定價 (元)'),
    unit_price_twd: z.number().int().min(0).describe('單價 (元)'),
    is_active: z
        .number()
        .int()
        .min(0)
        .max(1)
        .describe('是否啟用 (0: 停用, 1: 啟用)'),
    type: ProductTypeSchema.default('physical').describe('商品類型'),
    /** 是否為多規格品項；為 true 時價格/庫存/SKU 以 variants 為準 */
    has_variants: z.boolean().default(false).describe('是否有規格（多規格品項）'),
    metadata: ProductMetadataSchema.describe('商品特有欄位（JSONB）'),
    recommendation_badge: RecommendationBadgeSchema.describe('推薦標籤（lucide icon key）'),
    // 向後兼容欄位（從 metadata 映射，僅在 API 回應中使用）
    stock_quantity: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('當前庫存量（從 metadata 映射）'),
    safety_stock: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('安全庫存水位（從 metadata 映射）'),
    avg_cost: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('平均成本 (分)（從 metadata 映射）'),
    is_inventory_tracked: z
        .number()
        .int()
        .min(0)
        .max(1)
        .optional()
        .describe('是否追蹤庫存 (0: 否, 1: 是)（從 metadata 映射）'),
    package_count: z
        .number()
        .int()
        .min(1)
        .nullable()
        .optional()
        .describe('服務包包含的次數（從 metadata 映射）'),
    valid_days: z
        .number()
        .int()
        .min(0)
        .default(0)
        .optional()
        .describe('有效天數（0 代表不過期）（從 metadata 映射）'),
    created_at: z.string().describe('建立時間 (UTC)'),
    updated_at: z.string().describe('更新時間 (UTC)'),
    current_month_sales: z.number().int().min(0).describe('本月賣出數量'),
    last_month_sales: z.number().int().min(0).describe('上月賣出數量'),
    /** 多規格品項時附上的規格列表 */
    variants: z.array(ProductVariantSchema).optional().describe('規格列表'),
    /** 套餐品項時附上的子商品明細（type 為 bundle 時由後端帶出） */
    bundle_items: z
        .array(BundleItemWithProductSchema)
        .optional()
        .describe('套餐明細（子商品 + 數量）'),
});
/**
 * 建立產品請求 Schema
 * 支援兩種格式：
 * 1. 新格式：直接提供 metadata 物件
 * 2. 舊格式：提供特有欄位（向後兼容，會自動組裝到 metadata）
 */
export const CreateProductRequestSchema = z
    .object({
    sku: z.string().min(1).max(50).describe('產品 SKU'),
    category: z
        .string()
        .max(100)
        .optional()
        .describe('產品分類（單一分類時使用，與 categories 二擇一）'),
    /** 多分類；若有提供則以 categories 為準，否則以 category 作為單一元素 */
    categories: z
        .array(z.string().max(100))
        .optional()
        .describe('產品所屬分類列表'),
    name: z.string().min(1).max(200).describe('產品名稱'),
    description: z.string().max(1000).optional().describe('產品描述'),
    img_url: z
        .string()
        .optional()
        .or(z.literal(''))
        .describe('產品主圖 URL（可以是完整 URL 或相對路徑）'),
    /** 多張圖片 URL（主圖為 img_url；此為輪播/相簿用） */
    image_urls: z.array(z.string()).optional().describe('多張圖片 URL 列表'),
    list_price_twd: z.number().int().min(0).describe('定價 (元)'),
    unit_price_twd: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('單價 (元)；未填時預設與定價相同'),
    is_active: z
        .number()
        .int()
        .min(0)
        .max(1)
        .default(1)
        .describe('是否啟用 (0: 停用, 1: 啟用)'),
    type: ProductTypeSchema.default('physical').optional().describe('商品類型'),
    /** 是否為多規格品項；為 true 時需提供 specs 與 variants */
    has_variants: z
        .boolean()
        .default(false)
        .optional()
        .describe('是否有規格（多規格品項）'),
    /** 規格定義（規格名 + 選項）；has_variants 為 true 時必填 */
    specs: z
        .array(ProductSpecSchema)
        .min(1)
        .optional()
        .describe('規格定義（如顏色、尺寸）'),
    /** 規格矩陣；has_variants 為 true 時必填至少一筆 */
    variants: z
        .array(ProductVariantSchema)
        .min(1)
        .optional()
        .describe('規格列表（每組組合一筆）'),
    metadata: ProductMetadataSchema.optional().describe('商品特有欄位（JSONB，新格式）'),
    recommendation_badge: RecommendationBadgeSchema.describe('推薦標籤（lucide icon key）'),
    // 向後兼容：舊格式特有欄位（會自動組裝到 metadata）
    stock_quantity: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('當前庫存量（舊格式，會組裝到 metadata）'),
    safety_stock: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('安全庫存水位（舊格式，會組裝到 metadata）'),
    avg_cost: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('平均成本 (分)（舊格式，會組裝到 metadata）'),
    is_inventory_tracked: z
        .number()
        .int()
        .min(0)
        .max(1)
        .optional()
        .describe('是否追蹤庫存 (0: 否, 1: 是)（舊格式，會組裝到 metadata）'),
    package_count: z
        .number()
        .int()
        .min(1)
        .nullable()
        .optional()
        .describe('服務包包含的次數（舊格式，會組裝到 metadata）'),
    valid_days: z
        .number()
        .int()
        .min(0)
        .default(0)
        .optional()
        .describe('有效天數（0 代表不過期）（舊格式，會組裝到 metadata）'),
    /** 套餐明細（type 為 bundle 時必填至少一筆） */
    bundle_items: z
        .array(BundleItemSchema)
        .min(1)
        .optional()
        .describe('套餐明細（子商品 ID + 數量）'),
})
    .refine(data => {
    if (data.has_variants) {
        return (Array.isArray(data.specs) &&
            data.specs.length >= 1 &&
            Array.isArray(data.variants) &&
            data.variants.length >= 1);
    }
    return true;
}, {
    message: 'has_variants 為 true 時，specs 與 variants 為必填且至少一筆',
    path: ['variants'],
})
    .refine(data => {
    if (data.type === 'bundle') {
        return (Array.isArray(data.bundle_items) && data.bundle_items.length >= 1);
    }
    return true;
}, {
    message: 'type 為 bundle 時，bundle_items 為必填且至少一筆',
    path: ['bundle_items'],
});
/**
 * 更新產品請求 Schema
 * 支援兩種格式：
 * 1. 新格式：直接提供 metadata 物件
 * 2. 舊格式：提供特有欄位（向後兼容，會自動組裝到 metadata）
 */
export const UpdateProductRequestSchema = z.object({
    sku: z.string().min(1).max(50).optional().describe('產品 SKU'),
    category: z
        .string()
        .max(100)
        .optional()
        .describe('產品分類（單一分類時使用，與 categories 二擇一）'),
    /** 多分類；若有提供則覆寫所屬分類列表 */
    categories: z
        .array(z.string().max(100))
        .optional()
        .describe('產品所屬分類列表'),
    name: z.string().min(1).max(200).optional().describe('產品名稱'),
    description: z.string().max(1000).optional().describe('產品描述'),
    img_url: z
        .string()
        .optional()
        .or(z.literal(''))
        .describe('產品主圖 URL（可以是完整 URL 或相對路徑）'),
    /** 多張圖片 URL（主圖為 img_url；此為輪播/相簿用） */
    image_urls: z.array(z.string()).optional().describe('多張圖片 URL 列表'),
    list_price_twd: z.number().int().min(0).optional().describe('定價 (元)'),
    unit_price_twd: z.number().int().min(0).optional().describe('單價 (元)'),
    is_active: z
        .number()
        .int()
        .min(0)
        .max(1)
        .optional()
        .describe('是否啟用 (0: 停用, 1: 啟用)'),
    type: ProductTypeSchema.optional().describe('商品類型'),
    has_variants: z.boolean().optional().describe('是否有規格（多規格品項）'),
    specs: z
        .array(ProductSpecSchema)
        .min(1)
        .optional()
        .describe('規格定義（has_variants 為 true 時）'),
    variants: z
        .array(ProductVariantSchema)
        .min(1)
        .optional()
        .describe('規格列表（has_variants 為 true 時）'),
    metadata: ProductMetadataSchema.optional().describe('商品特有欄位（JSONB，新格式）'),
    recommendation_badge: RecommendationBadgeSchema.describe('推薦標籤（lucide icon key）'),
    // 向後兼容：舊格式特有欄位（會自動組裝到 metadata）
    stock_quantity: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('當前庫存量（舊格式，會組裝到 metadata）'),
    safety_stock: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('安全庫存水位（舊格式，會組裝到 metadata）'),
    avg_cost: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('平均成本 (分)（舊格式，會組裝到 metadata）'),
    is_inventory_tracked: z
        .number()
        .int()
        .min(0)
        .max(1)
        .optional()
        .describe('是否追蹤庫存 (0: 否, 1: 是)（舊格式，會組裝到 metadata）'),
    package_count: z
        .number()
        .int()
        .min(1)
        .nullable()
        .optional()
        .describe('服務包包含的次數（舊格式，會組裝到 metadata）'),
    valid_days: z
        .number()
        .int()
        .min(0)
        .default(0)
        .optional()
        .describe('有效天數（0 代表不過期）（舊格式，會組裝到 metadata）'),
    /** 套餐明細（type 為 bundle 時可送出新陣列覆寫） */
    bundle_items: z
        .array(BundleItemSchema)
        .min(1)
        .optional()
        .describe('套餐明細（子商品 ID + 數量）'),
});
/** 將前端可能傳的字串 "undefined" 或空字串正規化為 undefined，避免進入 WHERE 條件 */
const optionalQueryString = (desc) => z.preprocess(v => (v === 'undefined' || v === '' ? undefined : v), z.string().optional().describe(desc));
/**
 * 產品查詢參數 Schema
 * 查詢參數中字串 "undefined" 或空字串會正規化為 undefined，不參與篩選。
 */
export const ProductQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1).describe('頁碼 (從 1 開始)'),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(100)
        .default(20)
        .describe('每頁筆數 (1-100)'),
    sortBy: ProductSortBySchema.default('id').describe('排序欄位'),
    sortDir: SortDirectionSchema.default('asc').describe('排序方向'),
    search: optionalQueryString('SKU 或名稱搜尋'),
    category: optionalQueryString('分類篩選'),
    type: z.preprocess(v => (v === 'undefined' || v === '' ? undefined : v), ProductTypeSchema.optional().describe('商品類型篩選')),
    is_active: z.preprocess(v => (v === 'undefined' || v === '' ? undefined : v), z.coerce.number().int().min(0).max(1).optional().describe('啟用狀態篩選')),
    from: optionalQueryString('建立時間起始 (台北時間)'),
    to: optionalQueryString('建立時間結束 (台北時間)'),
});
// PaginationSchema 已從 ./common.js 導入
/**
 * 產品列表回應 Schema
 */
export const ProductListResponseSchema = z.object({
    success: z.boolean().describe('操作是否成功'),
    data: z.array(ProductSchema).describe('產品列表'),
    pagination: PaginationSchema.describe('分頁資訊'),
    timestamp: z.string().describe('回應時間戳'),
});
/**
 * 產品詳細回應 Schema
 */
export const ProductDetailResponseSchema = z.object({
    success: z.boolean().describe('操作是否成功'),
    data: ProductSchema.describe('產品詳細資訊'),
    timestamp: z.string().describe('回應時間戳'),
});
/**
 * 產品建立回應 Schema
 */
export const ProductCreateResponseSchema = z.object({
    success: z.boolean().describe('操作是否成功'),
    data: ProductSchema.describe('建立的產品資訊'),
    timestamp: z.string().describe('回應時間戳'),
});
/**
 * 產品更新回應 Schema
 */
export const ProductUpdateResponseSchema = z.object({
    success: z.boolean().describe('操作是否成功'),
    data: ProductSchema.describe('更新的產品資訊'),
    timestamp: z.string().describe('回應時間戳'),
});
/**
 * 產品刪除回應 Schema
 */
export const ProductDeleteResponseSchema = z.object({
    success: z.boolean().describe('操作是否成功'),
    message: z.string().describe('刪除成功訊息'),
    timestamp: z.string().describe('回應時間戳'),
});
/**
 * R2 上傳請求 Schema
 * 注意：在瀏覽器環境中使用 File 類型，在 Node.js 環境中使用 Blob 或 Buffer
 * 此 schema 使用 z.any() 以支援多種環境
 */
export const R2UploadRequestSchema = z.object({
    file: z
        .any()
        .describe('WebP 檔案（瀏覽器環境為 File，Node.js 環境為 Blob 或 Buffer）'),
});
/**
 * R2 上傳回應 Schema
 */
export const R2UploadResponseSchema = z.object({
    success: z.boolean().describe('操作是否成功'),
    data: z
        .object({
        key: z.string().describe('檔案 key'),
        public_url: z
            .string()
            .url()
            .optional()
            .describe('公開 URL（如果有設定）'),
    })
        .describe('上傳結果'),
    timestamp: z.string().describe('回應時間戳'),
});
// Pagination, ErrorResponse 類型已從 ./common.js 導入
